/**
 * 드롭·선택된 항목을 "올릴 파일 목록"으로 펼친다.
 *
 * 폴더를 통째로 올리려면 두 가지가 필요하다.
 *  1) 폴더 안 훑기 — 드롭은 DataTransferItem.webkitGetAsEntry() 로 직접 내려가야
 *     한다(dataTransfer.files 에는 폴더가 그냥 빠진다). 선택은
 *     <input webkitdirectory> 가 이미 평평한 FileList 를 준다.
 *  2) 어느 폴더의 파일인지 남기기 — File.name 에 상대 경로를 넣는다.
 *     webkitRelativePath 는 읽기 전용이라 드롭한 파일에 채울 수 없고, 별도
 *     필드를 달면 다이얼로그·페이지·서비스까지 타입이 번진다. 서버는 이 이름을
 *     original_name 으로 저장한다.
 */

/**
 * 폴더 업로드 문서는 이름이 "폴더/하위/파일.ext" 다. 한 줄에 다 넣으면 말줄임이
 * 정작 파일명을 지우므로, 목록에서는 파일명과 폴더를 따로 보여 준다.
 */
export const documentBaseName = (name: string): string => name.slice(name.lastIndexOf('/') + 1);

/** 같은 이름에서 폴더 부분만. 경로가 없으면 빈 문자열. */
export const documentFolder = (name: string): string => {
  const slash = name.lastIndexOf('/');
  return slash < 0 ? '' : name.slice(0, slash);
};

/** 폴더를 열면 항상 따라오는 OS 부산물. 보관함에 남을 이유가 없다. */
const JUNK = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);

/** 이름에 폴더 경로를 담은 새 File. 경로가 없으면 원본을 그대로 돌려준다. */
export function withFolderPath(file: File, path: string): File {
  const clean = path.replace(/^\/+/, '');
  if (!clean.includes('/')) return file;
  return new File([file], clean, { type: file.type, lastModified: file.lastModified });
}

const isJunk = (name: string): boolean => JUNK.has(name.split('/').pop() || name);

/** <input type="file"> 결과. webkitdirectory 로 고른 경우 상대 경로를 붙인다. */
export function filesFromInput(list: FileList | File[] | null): File[] {
  return Array.from(list || [])
    .filter((f) => !isJunk(f.name))
    .map((f) => withFolderPath(f, f.webkitRelativePath || ''));
}

/** 드롭된 항목. 폴더면 안쪽까지 재귀로 훑는다. */
export async function filesFromDrop(dt: DataTransfer): Promise<File[]> {
  // items 는 이벤트 핸들러가 끝나면 비워지므로 await 이전에 동기적으로 꺼내 둔다.
  const entries: FileSystemEntry[] = [];
  for (const item of Array.from(dt.items || [])) {
    const entry = item.webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }
  // 항목 API 가 없는 환경 — 폴더는 못 받지만 파일 드롭은 예전처럼 동작해야 한다.
  if (entries.length === 0) return filesFromInput(dt.files);

  const out: File[] = [];
  for (const entry of entries) await collect(entry, out);
  return out;
}

async function collect(entry: FileSystemEntry, out: File[]): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File | null>((resolve) =>
      (entry as FileSystemFileEntry).file(resolve, () => resolve(null)),
    );
    // fullPath 는 "/폴더/파일" 처럼 앞에 / 가 붙는다 — withFolderPath 가 떼 준다.
    if (file && !isJunk(file.name)) out.push(withFolderPath(file, entry.fullPath));
    return;
  }
  if (!entry.isDirectory) return;

  const reader = (entry as FileSystemDirectoryEntry).createReader();
  // readEntries 는 한 번에 최대 100개만 준다 — 빈 배열이 올 때까지 반복해야
  // 하위 파일이 100개를 넘는 폴더가 잘리지 않는다.
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve) =>
      reader.readEntries(resolve, () => resolve([])),
    );
    if (batch.length === 0) return;
    for (const child of batch) await collect(child, out);
  }
}
